/**
 * The server counts a form whose components are all Notes or the school logo as a communication,
 * and clears every linked setting when it saves one. This is the same rule, so a school can be
 * told before that happens rather than afterwards.
 *
 * Three places make a form live or save it: the editor, the setup step the wizard shows after
 * creation, and the Make form live row action on the Forms list. All read the rule from here.
 */

/** The editor holds the schema as an array. The API hands it back as a JSON string. */
const asComponents = (schema) => {
    if (Array.isArray(schema)) {
        return schema;
    }

    if (typeof schema !== 'string') {
        return [];
    }

    try {
        const parsed = JSON.parse(schema);

        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const becomesCommunication = (schema) => {
    const components = asComponents(schema);

    if (components.length === 0) {
        return false;
    }

    return components.every(
        (component) => component?.type === 'additionalNotes' || component?.type === 'schoolLogo'
    );
};

export const settingsClearedOnSave = ({
    linkedToStudents,
    linkedToEmployees,
    linkedStrictFiltering,
    allLinkedEmployeesCanSubmit,
    showResponsesInLinkedProfile,
    showResponsesToLinkedParents,
    selfService,
}) => {
    const lost = [];

    if (linkedToStudents) {
        lost.push('the linked student');
    }

    if (linkedToEmployees) {
        lost.push('the linked employee');
    }

    if (linkedStrictFiltering) {
        lost.push('the restriction to the linked person’s own groups');
    }

    if (allLinkedEmployeesCanSubmit) {
        lost.push('All linked employees can submit');
    }

    if (showResponsesInLinkedProfile) {
        lost.push('Show submitted responses on the linked person’s profile');
    }

    if (showResponsesToLinkedParents) {
        lost.push('Show responses to linked parents');
    }

    if (selfService) {
        lost.push('Self-service');
    }

    return lost;
};

/** The settings going live clears on this form, as the API returns it. Empty when it stays a form. */
export const settingsClearedByGoingLive = (form) => {
    if (!form || !becomesCommunication(form.schema ?? form.Schema)) {
        return [];
    }

    return settingsClearedOnSave({
        linkedToStudents: !!(form.linkToStudents ?? form.LinkToStudents),
        linkedToEmployees: !!(form.linkToEmployees ?? form.LinkToEmployees),
        linkedStrictFiltering: !!(form.linkedStrictFiltering ?? form.LinkedStrictFiltering),
        allLinkedEmployeesCanSubmit: !!(form.allLinkedEmployeesCanSubmit ?? form.AllLinkedEmployeesCanSubmit),
        showResponsesInLinkedProfile: !!(form.showResponsesInLinkedProfile ?? form.ShowResponsesInLinkedProfile),
        showResponsesToLinkedParents: !!(form.showResponsesToLinkedParents ?? form.ShowResponsesToLinkedParents),
        selfService: !!(form.allowMultipleSubmissions ?? form.AllowMultipleSubmissions),
    });
};

export const communicationWarning = (action, lost) =>
    'This form holds only notes, so ' + action + ' turns it into an email communication. ' +
    'These settings are cleared when that happens: ' +
    lost.join(', ') +
    '. Add a question to keep it as a form.';

// AD-2634: a save asks for confirmation only where it turns the form into an email communication
export const needsSaveConfirmation = settingsLostOnSave => (settingsLostOnSave?.length ?? 0) > 0;
